// src/components/products/ProductForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createProduct,
  updateProduct,
  fetchProduct,
  fetchEntities,
  addFeature,
  updateFeature,
  deleteFeature,
  addBenefit,
  updateBenefit,
  deleteBenefit,
  addPricingTier,
  updatePricingTier,
  deletePricingTier,
  clearProductError,
  clearProductSuccess,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import DocumentUploadModal from '../common/DocumentUploadModal';
import DocumentGallery from '../common/DocumentGallery';

const ProductForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedProduct, loading, error, success, entities } = useSelector(
    (state) => state.products
  );

  // Main product form state
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    entityId: '',
    description: '',
    detailedDescription: '',
    icon: '',
    color: '#2563eb',
    image: '',
    minAge: '',
    maxAge: '',
    popular: false,
    new: false,
    isActive: true,
    order: 0,
  });

  // Related collections state
  const [features, setFeatures] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [documents, setDocuments] = useState([]);

  // UI state
  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Feature form state
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureForm, setFeatureForm] = useState({
    title: '',
    description: '',
    icon: '',
    order: 0,
  });

  // Benefit form state
  const [showBenefitForm, setShowBenefitForm] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [benefitForm, setBenefitForm] = useState({
    title: '',
    amount: '',
    description: '',
    order: 0,
  });

  // Pricing tier form state
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    tierName: '',
    monthlyPremium: '',
    coverage: '',
    features: [],
    recommended: false,
    order: 0,
  });
  const [pricingFeatureInput, setPricingFeatureInput] = useState('');

  const isEditMode = !!id;

  const tabs = [
    { id: 'basic', label: 'Informations de base' },
    { id: 'details', label: 'Détails' },
    { id: 'features', label: `Caractéristiques (${features.length})` },
    { id: 'benefits', label: `Avantages (${benefits.length})` },
    { id: 'pricing', label: `Tarifs (${pricingTiers.length})` },
    { id: 'documents', label: `Documents (${documents.length})` },
    { id: 'settings', label: 'Paramètres' },
  ];

  const iconOptions = [
    '📦', '🚗', '🏠', '🏥', '💼', '📱', '💻', '🔧', 
    '⚙️', '💡', '💰', '📊', '🔒', '🔑', '📝', '📋',
    '👥', '🏢', '🌍', '⚕️', '🎓', '🏦', '🛡️', '⭐'
  ];

  const colorOptions = [
    '#2563eb', // blue
    '#dc2626', // red
    '#059669', // green
    '#d97706', // orange
    '#7c3aed', // purple
    '#db2777', // pink
    '#6b7280', // gray
    '#0891b2', // cyan
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      overflowX: 'auto',
    },
    tab: {
      padding: '1rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
      backgroundColor: '#ffffff',
    },
    content: {
      padding: '2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    iconGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    iconButton: {
      padding: '0.75rem',
      fontSize: '1.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    iconButtonSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#dbeafe',
      transform: 'scale(1.05)',
    },
    colorGrid: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      marginTop: '0.5rem',
    },
    colorButton: {
      width: '40px',
      height: '40px',
      borderRadius: '0.375rem',
      border: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    colorButtonSelected: {
      borderColor: '#111827',
      transform: 'scale(1.1)',
    },
    imageUpload: {
      marginTop: '1rem',
    },
    imagePreview: {
      width: '150px',
      height: '150px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#f3f4f6',
      border: '1px solid #e5e7eb',
      marginBottom: '1rem',
    },
    imageUploadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    checkboxGroup: {
      display: 'flex',
      gap: '2rem',
      flexWrap: 'wrap',
    },
    // Section styles for related items
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      paddingBottom: '0.5rem',
      borderBottom: '2px solid #f3f4f6',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    addButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#10b981',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    addButtonHover: {
      backgroundColor: '#059669',
    },
    itemsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem',
    },
    itemCard: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      position: 'relative',
    },
    itemCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    itemTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    itemSubtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    itemContent: {
      fontSize: '0.875rem',
      color: '#374151',
      marginBottom: '0.5rem',
    },
    itemMeta: {
      display: 'flex',
      gap: '1rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    itemActions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    iconButton: {
      padding: '0.25rem',
      background: 'none',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'all 0.2s',
    },
    editButton: {
      color: '#4f46e5',
    },
    deleteButton: {
      color: '#dc2626',
    },
    // Modal styles for inline forms
    modal: {
      marginTop: '1rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      marginBottom: '1.5rem',
    },
    modalTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '1rem',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    cancelModalButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    pricingTier: {
      padding: '1rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    pricingHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    pricingName: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
    },
    pricingBadge: {
      padding: '0.25rem 0.75rem',
      backgroundColor: '#fbbf24',
      color: '#92400e',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    pricingAmount: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: '#059669',
      marginBottom: '0.5rem',
    },
    pricingCoverage: {
      fontSize: '0.875rem',
      color: '#374151',
      marginBottom: '0.5rem',
    },
    featureTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    featureTag: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    featureInput: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
      paddingTop: '2rem',
      borderTop: '1px solid #e5e7eb',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  // Load initial data
  useEffect(() => {
    dispatch(fetchEntities({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id, isEditMode]);

  // Populate form with selected product data
  useEffect(() => {
    if (isEditMode && selectedProduct) {
      setFormData({
        name: selectedProduct.name || '',
        shortName: selectedProduct.shortName || '',
        entityId: selectedProduct.entityId || '',
        description: selectedProduct.description || '',
        detailedDescription: selectedProduct.detailedDescription || '',
        icon: selectedProduct.icon || '',
        color: selectedProduct.color || '#2563eb',
        image: selectedProduct.image || '',
        minAge: selectedProduct.minAge || '',
        maxAge: selectedProduct.maxAge || '',
        popular: selectedProduct.popular || false,
        new: selectedProduct.new || false,
        isActive: selectedProduct.isActive ?? true,
        order: selectedProduct.order || 0,
      });
      setFeatures(selectedProduct.features || []);
      setBenefits(selectedProduct.benefits || []);
      setPricingTiers(selectedProduct.pricingTiers || []);
      setDocuments(selectedProduct.documents || []);
    }
  }, [selectedProduct, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearProductError());
      dispatch(clearProductSuccess());
    };
  }, [dispatch]);

  // Main form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du produit est requis';
    }

    if (!formData.entityId) {
      newErrors.entityId = "L'entité est requise";
    }

    if (formData.minAge && (formData.minAge < 0 || formData.minAge > 120)) {
      newErrors.minAge = 'L\'âge minimum doit être entre 0 et 120 ans';
    }

    if (formData.maxAge && (formData.maxAge < 0 || formData.maxAge > 120)) {
      newErrors.maxAge = 'L\'âge maximum doit être entre 0 et 120 ans';
    }

    if (formData.minAge && formData.maxAge && 
        parseInt(formData.minAge) > parseInt(formData.maxAge)) {
      newErrors.maxAge = 'L\'âge maximum doit être supérieur à l\'âge minimum';
    }

    return newErrors;
  }, [formData]);

  // Main form handlers
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleNumberChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseInt(value),
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleIconSelect = (icon) => {
    setFormData(prev => ({ ...prev, icon }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, color }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  // Feature handlers
  const handleAddFeature = () => {
    setEditingFeature(null);
    setFeatureForm({ title: '', description: '', icon: '', order: features.length });
    setShowFeatureForm(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature);
    setFeatureForm({
      title: feature.title,
      description: feature.description,
      icon: feature.icon || '',
      order: feature.order || 0,
    });
    setShowFeatureForm(true);
  };

  const handleSaveFeature = async () => {
    if (!featureForm.title) return;

    if (editingFeature) {
      // Update existing feature
      const updatedFeatures = features.map(f =>
        f.id === editingFeature.id ? { ...f, ...featureForm } : f
      );
      setFeatures(updatedFeatures);
      
      if (isEditMode) {
        try {
          await dispatch(updateFeature(id, editingFeature.id, featureForm));
        } catch (error) {
          console.error('Failed to update feature:', error);
        }
      }
    } else {
      // Add new feature
      const newFeature = {
        id: `temp-${Date.now()}`,
        ...featureForm,
        productId: id,
      };
      setFeatures([...features, newFeature]);
      
      if (isEditMode) {
        try {
          await dispatch(addFeature(id, featureForm));
        } catch (error) {
          console.error('Failed to add feature:', error);
        }
      }
    }

    setShowFeatureForm(false);
    setEditingFeature(null);
    setFeatureForm({ title: '', description: '', icon: '', order: 0 });
  };

  const handleDeleteFeature = async (featureId) => {
    setFeatures(features.filter(f => f.id !== featureId));
    
    if (isEditMode && !featureId.toString().startsWith('temp-')) {
      try {
        await dispatch(deleteFeature(id, featureId));
      } catch (error) {
        console.error('Failed to delete feature:', error);
      }
    }
  };

  // Benefit handlers
  const handleAddBenefit = () => {
    setEditingBenefit(null);
    setBenefitForm({ title: '', amount: '', description: '', order: benefits.length });
    setShowBenefitForm(true);
  };

  const handleEditBenefit = (benefit) => {
    setEditingBenefit(benefit);
    setBenefitForm({
      title: benefit.title,
      amount: benefit.amount || '',
      description: benefit.description,
      order: benefit.order || 0,
    });
    setShowBenefitForm(true);
  };

  const handleSaveBenefit = async () => {
    if (!benefitForm.title) return;

    if (editingBenefit) {
      const updatedBenefits = benefits.map(b =>
        b.id === editingBenefit.id ? { ...b, ...benefitForm } : b
      );
      setBenefits(updatedBenefits);
      
      if (isEditMode) {
        try {
          await dispatch(updateBenefit(id, editingBenefit.id, benefitForm));
        } catch (error) {
          console.error('Failed to update benefit:', error);
        }
      }
    } else {
      const newBenefit = {
        id: `temp-${Date.now()}`,
        ...benefitForm,
        productId: id,
      };
      setBenefits([...benefits, newBenefit]);
      
      if (isEditMode) {
        try {
          await dispatch(addBenefit(id, benefitForm));
        } catch (error) {
          console.error('Failed to add benefit:', error);
        }
      }
    }

    setShowBenefitForm(false);
    setEditingBenefit(null);
    setBenefitForm({ title: '', amount: '', description: '', order: 0 });
  };

  const handleDeleteBenefit = async (benefitId) => {
    setBenefits(benefits.filter(b => b.id !== benefitId));
    
    if (isEditMode && !benefitId.toString().startsWith('temp-')) {
      try {
        await dispatch(deleteBenefit(id, benefitId));
      } catch (error) {
        console.error('Failed to delete benefit:', error);
      }
    }
  };

  // Pricing tier handlers
  const handleAddPricing = () => {
    setEditingPricing(null);
    setPricingForm({
      tierName: '',
      monthlyPremium: '',
      coverage: '',
      features: [],
      recommended: false,
      order: pricingTiers.length,
    });
    setPricingFeatureInput('');
    setShowPricingForm(true);
  };

  const handleEditPricing = (tier) => {
    setEditingPricing(tier);
    setPricingForm({
      tierName: tier.tierName,
      monthlyPremium: tier.monthlyPremium,
      coverage: tier.coverage,
      features: tier.features || [],
      recommended: tier.recommended || false,
      order: tier.order || 0,
    });
    setPricingFeatureInput('');
    setShowPricingForm(true);
  };

  const handleAddPricingFeature = () => {
    if (pricingFeatureInput.trim()) {
      setPricingForm(prev => ({
        ...prev,
        features: [...prev.features, pricingFeatureInput.trim()]
      }));
      setPricingFeatureInput('');
    }
  };

  const handleRemovePricingFeature = (index) => {
    setPricingForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSavePricing = async () => {
    if (!pricingForm.tierName || !pricingForm.monthlyPremium) return;

    if (editingPricing) {
      const updatedTiers = pricingTiers.map(t =>
        t.id === editingPricing.id ? { ...t, ...pricingForm } : t
      );
      setPricingTiers(updatedTiers);
      
      if (isEditMode) {
        try {
          await dispatch(updatePricingTier(id, editingPricing.id, pricingForm));
        } catch (error) {
          console.error('Failed to update pricing tier:', error);
        }
      }
    } else {
      const newTier = {
        id: `temp-${Date.now()}`,
        ...pricingForm,
        productId: id,
      };
      setPricingTiers([...pricingTiers, newTier]);
      
      if (isEditMode) {
        try {
          await dispatch(addPricingTier(id, pricingForm));
        } catch (error) {
          console.error('Failed to add pricing tier:', error);
        }
      }
    }

    setShowPricingForm(false);
    setEditingPricing(null);
    setPricingForm({
      tierName: '',
      monthlyPremium: '',
      coverage: '',
      features: [],
      recommended: false,
      order: 0,
    });
  };

  const handleDeletePricing = async (tierId) => {
    setPricingTiers(pricingTiers.filter(t => t.id !== tierId));
    
    if (isEditMode && !tierId.toString().startsWith('temp-')) {
      try {
        await dispatch(deletePricingTier(id, tierId));
      } catch (error) {
        console.error('Failed to delete pricing tier:', error);
      }
    }
  };

  // Document handlers
  const handleDocumentUploadComplete = (uploadedDocs) => {
    setDocuments(prev => [...prev, ...uploadedDocs]);
  };

  const handleDocumentDelete = (doc) => {
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
  };

  // Submit handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setActiveTab('basic');
      return;
    }

    setIsSubmitting(true);

    try {
      const productData = {
        ...formData,
        minAge: formData.minAge || null,
        maxAge: formData.maxAge || null,
      };

      let savedProduct;
      if (isEditMode) {
        savedProduct = await dispatch(updateProduct(id, productData));
      } else {
        savedProduct = await dispatch(createProduct(productData));
      }

      // Save features, benefits, and pricing if they have temp IDs
      if (savedProduct?.id) {
        // Save features
        for (const feature of features) {
          if (feature.id.toString().startsWith('temp-')) {
            const { id, ...featureData } = feature;
            await dispatch(addFeature(savedProduct.id, featureData));
          }
        }

        // Save benefits
        for (const benefit of benefits) {
          if (benefit.id.toString().startsWith('temp-')) {
            const { id, ...benefitData } = benefit;
            await dispatch(addBenefit(savedProduct.id, benefitData));
          }
        }

        // Save pricing tiers
        for (const tier of pricingTiers) {
          if (tier.id.toString().startsWith('temp-')) {
            const { id, ...tierData } = tier;
            await dispatch(addPricingTier(savedProduct.id, tierData));
          }
        }
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm, features, benefits, pricingTiers]);

  const handleCancel = useCallback(() => {
    const hasChanges = Object.keys(formData).some(key => {
      if (key === 'order') return formData[key] !== 0;
      if (key === 'minAge' || key === 'maxAge') return formData[key] !== '';
      return formData[key] !== '' && formData[key] !== false;
    }) || features.length > 0 || benefits.length > 0 || pricingTiers.length > 0 || documents.length > 0;

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/products');
    }
  }, [formData, features, benefits, pricingTiers, documents, navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier le produit' : 'Créer un nouveau produit'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearProductSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Tabs */}
        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.content}>
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="name">
                    Nom du produit *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.name ? styles.inputError : {}),
                    }}
                    placeholder="Ex: Assurance Auto Premium"
                  />
                  {errors.name && (
                    <div style={styles.errorText}>{errors.name}</div>
                  )}
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="shortName">
                    Nom court
                  </label>
                  <input
                    type="text"
                    id="shortName"
                    name="shortName"
                    value={formData.shortName}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Ex: Auto Premium"
                  />
                  <div style={styles.helperText}>
                    Utilisé dans les espaces restreints
                  </div>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="entityId">
                  Entité *
                </label>
                <select
                  id="entityId"
                  name="entityId"
                  value={formData.entityId}
                  onChange={handleChange}
                  style={{
                    ...styles.select,
                    ...(errors.entityId ? styles.inputError : {}),
                  }}
                >
                  <option value="">Sélectionner une entité</option>
                  {entities.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
                {errors.entityId && (
                  <div style={styles.errorText}>{errors.entityId}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="order">
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  id="order"
                  name="order"
                  value={formData.order}
                  onChange={handleNumberChange}
                  min="0"
                  style={styles.input}
                />
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="description">
                    Description courte
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    style={styles.textarea}
                    placeholder="Brève description du produit (max 500 caractères)"
                    rows="3"
                  />
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="detailedDescription">
                    Description détaillée
                  </label>
                  <textarea
                    id="detailedDescription"
                    name="detailedDescription"
                    value={formData.detailedDescription}
                    onChange={handleChange}
                    style={styles.textarea}
                    placeholder="Description complète du produit"
                    rows="6"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div style={styles.grid}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Icône</label>
                <input
                  type="text"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="Ex: 📦"
                />
                <div style={styles.iconGrid}>
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      style={{
                        ...styles.iconButton,
                        ...(formData.icon === icon ? styles.iconButtonSelected : {}),
                      }}
                      onClick={() => handleIconSelect(icon)}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Couleur</label>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{ ...styles.input, height: '40px', padding: '0.25rem' }}
                />
                <div style={styles.colorGrid}>
                  {colorOptions.map(color => (
                    <button
                      key={color}
                      type="button"
                      style={{
                        ...styles.colorButton,
                        backgroundColor: color,
                        ...(formData.color === color ? styles.colorButtonSelected : {}),
                      }}
                      onClick={() => handleColorSelect(color)}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Image du produit</label>
                  {formData.image && (
                    <img
                      src={formData.image}
                      alt="Preview"
                      style={styles.imagePreview}
                    />
                  )}
                  <div style={styles.imageUpload}>
                    <input
                      type="file"
                      id="productImage"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('productImage').click()}
                      style={styles.imageUploadButton}
                    >
                      <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formData.image ? "Changer l'image" : 'Ajouter une image'}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="minAge">
                  Âge minimum
                </label>
                <input
                  type="number"
                  id="minAge"
                  name="minAge"
                  value={formData.minAge}
                  onChange={handleNumberChange}
                  min="0"
                  max="120"
                  style={{
                    ...styles.input,
                    ...(errors.minAge ? styles.inputError : {}),
                  }}
                  placeholder="0"
                />
                {errors.minAge && (
                  <div style={styles.errorText}>{errors.minAge}</div>
                )}
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="maxAge">
                  Âge maximum
                </label>
                <input
                  type="number"
                  id="maxAge"
                  name="maxAge"
                  value={formData.maxAge}
                  onChange={handleNumberChange}
                  min="0"
                  max="120"
                  style={{
                    ...styles.input,
                    ...(errors.maxAge ? styles.inputError : {}),
                  }}
                  placeholder="120"
                />
                {errors.maxAge && (
                  <div style={styles.errorText}>{errors.maxAge}</div>
                )}
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Caractéristiques du produit</h3>
                <button
                  type="button"
                  onClick={handleAddFeature}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter une caractéristique
                </button>
              </div>

              {/* Feature Form Modal */}
              {showFeatureForm && (
                <div style={styles.modal}>
                  <h4 style={styles.modalTitle}>
                    {editingFeature ? 'Modifier' : 'Ajouter'} une caractéristique
                  </h4>
                  <div style={styles.grid}>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Titre *</label>
                        <input
                          type="text"
                          value={featureForm.title}
                          onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: Garantie tous risques"
                        />
                      </div>
                    </div>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Description</label>
                        <textarea
                          value={featureForm.description}
                          onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })}
                          style={styles.textarea}
                          placeholder="Description de la caractéristique"
                          rows="3"
                        />
                      </div>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Icône</label>
                      <input
                        type="text"
                        value={featureForm.icon}
                        onChange={(e) => setFeatureForm({ ...featureForm, icon: e.target.value })}
                        style={styles.input}
                        placeholder="Ex: 🔧"
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Ordre</label>
                      <input
                        type="number"
                        value={featureForm.order}
                        onChange={(e) => setFeatureForm({ ...featureForm, order: parseInt(e.target.value) })}
                        style={styles.input}
                        min="0"
                      />
                    </div>
                  </div>
                  <div style={styles.modalActions}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowFeatureForm(false);
                        setEditingFeature(null);
                      }}
                      style={styles.cancelModalButton}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveFeature}
                      style={styles.saveButton}
                    >
                      {editingFeature ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              )}

              {/* Features List */}
              {features.length > 0 ? (
                <div style={styles.itemsGrid}>
                  {features.map((feature) => (
                    <div key={feature.id} style={styles.itemCard}>
                      <div style={styles.itemCardHeader}>
                        <span style={styles.itemTitle}>{feature.title}</span>
                        <div style={styles.itemActions}>
                          <button
                            type="button"
                            onClick={() => handleEditFeature(feature)}
                            style={{ ...styles.iconButton, ...styles.editButton }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFeature(feature.id)}
                            style={{ ...styles.iconButton, ...styles.deleteButton }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {feature.icon && (
                        <div style={styles.itemSubtitle}>Icône: {feature.icon}</div>
                      )}
                      <div style={styles.itemContent}>{feature.description}</div>
                      <div style={styles.itemMeta}>
                        <span>Ordre: {feature.order || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune caractéristique définie. Cliquez sur "Ajouter une caractéristique" pour commencer.
                </div>
              )}
            </div>
          )}

          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Avantages du produit</h3>
                <button
                  type="button"
                  onClick={handleAddBenefit}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter un avantage
                </button>
              </div>

              {/* Benefit Form Modal */}
              {showBenefitForm && (
                <div style={styles.modal}>
                  <h4 style={styles.modalTitle}>
                    {editingBenefit ? 'Modifier' : 'Ajouter'} un avantage
                  </h4>
                  <div style={styles.grid}>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Titre *</label>
                        <input
                          type="text"
                          value={benefitForm.title}
                          onChange={(e) => setBenefitForm({ ...benefitForm, title: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: Assistance 24h/24"
                        />
                      </div>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Montant</label>
                      <input
                        type="text"
                        value={benefitForm.amount}
                        onChange={(e) => setBenefitForm({ ...benefitForm, amount: e.target.value })}
                        style={styles.input}
                        placeholder="Ex: Jusqu'à 5M FCFA"
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Ordre</label>
                      <input
                        type="number"
                        value={benefitForm.order}
                        onChange={(e) => setBenefitForm({ ...benefitForm, order: parseInt(e.target.value) })}
                        style={styles.input}
                        min="0"
                      />
                    </div>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Description</label>
                        <textarea
                          value={benefitForm.description}
                          onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                          style={styles.textarea}
                          placeholder="Description de l'avantage"
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                  <div style={styles.modalActions}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBenefitForm(false);
                        setEditingBenefit(null);
                      }}
                      style={styles.cancelModalButton}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveBenefit}
                      style={styles.saveButton}
                    >
                      {editingBenefit ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              )}

              {/* Benefits List */}
              {benefits.length > 0 ? (
                <div style={styles.itemsGrid}>
                  {benefits.map((benefit) => (
                    <div key={benefit.id} style={styles.itemCard}>
                      <div style={styles.itemCardHeader}>
                        <span style={styles.itemTitle}>{benefit.title}</span>
                        <div style={styles.itemActions}>
                          <button
                            type="button"
                            onClick={() => handleEditBenefit(benefit)}
                            style={{ ...styles.iconButton, ...styles.editButton }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBenefit(benefit.id)}
                            style={{ ...styles.iconButton, ...styles.deleteButton }}
                          >
                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {benefit.amount && (
                        <div style={styles.itemSubtitle}>Montant: {benefit.amount}</div>
                      )}
                      <div style={styles.itemContent}>{benefit.description}</div>
                      <div style={styles.itemMeta}>
                        <span>Ordre: {benefit.order || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun avantage défini. Cliquez sur "Ajouter un avantage" pour commencer.
                </div>
              )}
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Tarifs</h3>
                <button
                  type="button"
                  onClick={handleAddPricing}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter un tarif
                </button>
              </div>

              {/* Pricing Form Modal */}
              {showPricingForm && (
                <div style={styles.modal}>
                  <h4 style={styles.modalTitle}>
                    {editingPricing ? 'Modifier' : 'Ajouter'} un tarif
                  </h4>
                  <div style={styles.grid}>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Nom du tarif *</label>
                        <input
                          type="text"
                          value={pricingForm.tierName}
                          onChange={(e) => setPricingForm({ ...pricingForm, tierName: e.target.value })}
                          style={styles.input}
                          placeholder="Ex: Formule Essentielle"
                        />
                      </div>
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Prime mensuelle * (FCFA)</label>
                      <input
                        type="number"
                        value={pricingForm.monthlyPremium}
                        onChange={(e) => setPricingForm({ ...pricingForm, monthlyPremium: e.target.value })}
                        style={styles.input}
                        min="0"
                        step="1000"
                      />
                    </div>
                    <div style={styles.fieldGroup}>
                      <label style={styles.label}>Ordre</label>
                      <input
                        type="number"
                        value={pricingForm.order}
                        onChange={(e) => setPricingForm({ ...pricingForm, order: parseInt(e.target.value) })}
                        style={styles.input}
                        min="0"
                      />
                    </div>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Couverture</label>
                        <textarea
                          value={pricingForm.coverage}
                          onChange={(e) => setPricingForm({ ...pricingForm, coverage: e.target.value })}
                          style={styles.textarea}
                          placeholder="Description de la couverture"
                          rows="2"
                        />
                      </div>
                    </div>
                    <div style={styles.fullWidth}>
                      <div style={styles.fieldGroup}>
                        <label style={styles.label}>Caractéristiques incluses</label>
                        <div style={styles.featureInput}>
                          <input
                            type="text"
                            value={pricingFeatureInput}
                            onChange={(e) => setPricingFeatureInput(e.target.value)}
                            style={{ ...styles.input, flex: 1 }}
                            placeholder="Ajouter une caractéristique"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPricingFeature())}
                          />
                          <button
                            type="button"
                            onClick={handleAddPricingFeature}
                            style={styles.imageUploadButton}
                          >
                            Ajouter
                          </button>
                        </div>
                        {pricingForm.features.length > 0 && (
                          <div style={styles.featureTags}>
                            {pricingForm.features.map((feature, index) => (
                              <span key={index} style={styles.featureTag}>
                                {feature}
                                <button
                                  type="button"
                                  onClick={() => handleRemovePricingFeature(index)}
                                  style={{ marginLeft: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={styles.fullWidth}>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={pricingForm.recommended}
                          onChange={(e) => setPricingForm({ ...pricingForm, recommended: e.target.checked })}
                          style={styles.checkbox}
                        />
                        Tarif recommandé
                      </label>
                    </div>
                  </div>
                  <div style={styles.modalActions}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPricingForm(false);
                        setEditingPricing(null);
                      }}
                      style={styles.cancelModalButton}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePricing}
                      style={styles.saveButton}
                    >
                      {editingPricing ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              )}

              {/* Pricing List */}
              {pricingTiers.length > 0 ? (
                <div>
                  {pricingTiers.map((tier) => (
                    <div key={tier.id} style={styles.pricingTier}>
                      <div style={styles.pricingHeader}>
                        <span style={styles.pricingName}>{tier.tierName}</span>
                        {tier.recommended && (
                          <span style={styles.pricingBadge}>Recommandé</span>
                        )}
                      </div>
                      <div style={styles.pricingAmount}>
                        {formatCurrency(tier.monthlyPremium)} <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mois</span>
                      </div>
                      <div style={styles.pricingCoverage}>
                        {tier.coverage}
                      </div>
                      {tier.features && tier.features.length > 0 && (
                        <div style={styles.featureTags}>
                          {tier.features.map((feature, index) => (
                            <span key={index} style={styles.featureTag}>
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={styles.itemActions}>
                        <button
                          type="button"
                          onClick={() => handleEditPricing(tier)}
                          style={{ ...styles.iconButton, ...styles.editButton }}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePricing(tier.id)}
                          style={{ ...styles.iconButton, ...styles.deleteButton }}
                        >
                          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucun tarif défini. Cliquez sur "Ajouter un tarif" pour commencer.
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Documents et ressources</h3>
                <button
                  type="button"
                  onClick={() => setShowDocumentModal(true)}
                  style={styles.addButton}
                >
                  <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Ajouter des documents
                </button>
              </div>

              <DocumentGallery
                documents={documents}
                entityId={id}
                entityType="product"
                onDelete={handleDocumentDelete}
              />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.checkboxGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      style={styles.checkbox}
                    />
                    Produit actif
                  </label>

                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="popular"
                      checked={formData.popular}
                      onChange={handleChange}
                      style={styles.checkbox}
                    />
                    Produit populaire
                  </label>

                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="new"
                      checked={formData.new}
                      onChange={handleChange}
                      style={styles.checkbox}
                    />
                    Nouveau produit
                  </label>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.helperText}>
                  <p><strong>Produit actif</strong> : Le produit est visible sur le site</p>
                  <p><strong>Produit populaire</strong> : Mis en avant avec un badge "Populaire"</p>
                  <p><strong>Nouveau produit</strong> : Mis en avant avec un badge "Nouveau"</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover?.backgroundColor || '#1d4ed8';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Création...') 
              : (isEditMode ? 'Mettre à jour le produit' : 'Créer le produit')}
          </button>
        </div>
      </form>

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <DocumentUploadModal
          entityId={id}
          entityType="product"
          onClose={() => setShowDocumentModal(false)}
          onUploadComplete={handleDocumentUploadComplete}
          multiple={true}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          maxSize={10 * 1024 * 1024} // 10MB
        />
      )}

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/products')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default ProductForm;